import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend 
} from 'recharts';
import { 
  TrendingUp, TrendingDown, Minus, DollarSign, ShoppingCart, 
  Package, Users, RefreshCw, Calendar, BarChart3, PieChart as PieChartIcon,
  Boxes, ArrowUpRight, ArrowDownRight, Download, FileText, CalendarClock
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import ScheduledReportsManager from './ScheduledReportsManager';

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

/**
 * RetailerAnalytics - Advanced analytics dashboard for retailers
 * 
 * Features:
 * - Sales trends with chart visualization
 * - Top items by quantity and revenue
 * - Inventory turnover metrics
 * - Timeframe selection (daily/weekly/monthly)
 */
export default function RetailerAnalytics() {
  const [timeframe, setTimeframe] = useState('weekly');
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [topItems, setTopItems] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadAnalytics();
  }, [timeframe]);

  const exportCSV = async () => {
    setExporting(true);
    try {
      const response = await api.get(`/analytics/retailer/export/csv?timeframe=${timeframe}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `retailer_analytics_${timeframe}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('CSV exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const exportPDF = async () => {
    setExporting(true);
    try {
      const response = await api.get(`/analytics/retailer/export/pdf?timeframe=${timeframe}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `retailer_analytics_${timeframe}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const [summaryRes, topItemsRes, inventoryRes] = await Promise.all([
        api.get(`/analytics/retailer/summary?timeframe=${timeframe}`),
        api.get(`/analytics/retailer/top-items?timeframe=${timeframe}`),
        api.get('/analytics/retailer/inventory-turnover')
      ]);
      
      setSummary(summaryRes.data);
      setTopItems(topItemsRes.data);
      setInventory(inventoryRes.data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const getTrendIcon = (direction) => {
    if (direction === 'up') return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (direction === 'down') return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  const formatChartData = (chartData) => {
    if (!chartData?.labels) return [];
    return chartData.labels.map((label, idx) => ({
      name: label,
      revenue: chartData.datasets?.[0]?.data?.[idx] || 0,
      orders: chartData.datasets?.[1]?.data?.[idx] || 0
    }));
  };

  const formatTopItemsData = (data) => {
    if (!data?.labels) return [];
    return data.labels.map((label, idx) => ({
      name: label.length > 20 ? label.substring(0, 20) + '...' : label,
      fullName: label,
      value: data.values?.[idx] || 0
    }));
  };

  if (loading && !summary) {
    return (
      <div className="space-y-6 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-blue-600" />
            Analytics Dashboard
          </h2>
          <p className="text-gray-500 text-sm">Track your store's performance</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={timeframe} onValueChange={setTimeframe}>
            <SelectTrigger className="w-32" data-testid="analytics-timeframe">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={loadAnalytics} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={exportCSV} 
            disabled={exporting || loading}
            data-testid="export-csv-btn"
          >
            <Download className={`w-4 h-4 mr-2 ${exporting ? 'animate-bounce' : ''}`} />
            CSV
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={exportPDF} 
            disabled={exporting || loading}
            data-testid="export-pdf-btn"
          >
            <FileText className={`w-4 h-4 mr-2 ${exporting ? 'animate-bounce' : ''}`} />
            PDF
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid md:grid-cols-4 gap-4">
          <Card data-testid="analytics-revenue-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Revenue</p>
                  <p className="text-2xl font-bold text-gray-900">
                    ${summary.summary.total_revenue.toLocaleString()}
                  </p>
                </div>
                <div className="bg-green-100 p-3 rounded-lg">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
              </div>
              <div className="flex items-center gap-1 mt-2 text-sm">
                {getTrendIcon(summary.trends.trend_direction)}
                <span className={summary.trends.revenue_change_percent >= 0 ? 'text-green-600' : 'text-red-600'}>
                  {summary.trends.revenue_change_percent >= 0 ? '+' : ''}{summary.trends.revenue_change_percent}%
                </span>
                <span className="text-gray-400">vs prev period</span>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="analytics-orders-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Orders</p>
                  <p className="text-2xl font-bold text-gray-900">{summary.summary.total_orders}</p>
                </div>
                <div className="bg-blue-100 p-3 rounded-lg">
                  <ShoppingCart className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <p className="text-sm text-gray-400 mt-2">
                {summary.summary.total_items_sold} items sold
              </p>
            </CardContent>
          </Card>

          <Card data-testid="analytics-conversion-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Conversion Rate</p>
                  <p className="text-2xl font-bold text-gray-900">{summary.summary.conversion_rate}%</p>
                </div>
                <div className="bg-purple-100 p-3 rounded-lg">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
              </div>
              <p className="text-sm text-gray-400 mt-2">
                {summary.summary.items_posted} items posted
              </p>
            </CardContent>
          </Card>

          <Card data-testid="analytics-savings-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Consumer Savings</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    ${summary.summary.total_consumer_savings.toLocaleString()}
                  </p>
                </div>
                <div className="bg-emerald-100 p-3 rounded-lg">
                  <Package className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
              <p className="text-sm text-gray-400 mt-2">
                Avg order: ${summary.summary.average_order_value}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts */}
      <Tabs defaultValue="trends" className="space-y-4">
        <TabsList>
          <TabsTrigger value="trends" data-testid="tab-trends">
            <BarChart3 className="w-4 h-4 mr-2" />
            Sales Trends
          </TabsTrigger>
          <TabsTrigger value="top-items" data-testid="tab-top-items">
            <PieChartIcon className="w-4 h-4 mr-2" />
            Top Items
          </TabsTrigger>
          <TabsTrigger value="inventory" data-testid="tab-inventory">
            <Boxes className="w-4 h-4 mr-2" />
            Inventory
          </TabsTrigger>
          <TabsTrigger value="scheduled" data-testid="tab-scheduled">
            <CalendarClock className="w-4 h-4 mr-2" />
            Scheduled Reports
          </TabsTrigger>
        </TabsList>

        {/* Sales Trends Tab */}
        <TabsContent value="trends">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Revenue & Orders Over Time</CardTitle>
            </CardHeader>
            <CardContent>
              {summary?.chart_data && (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={formatChartData(summary.chart_data)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                      formatter={(value, name) => [
                        name === 'revenue' ? `$${value}` : value,
                        name === 'revenue' ? 'Revenue' : 'Orders'
                      ]}
                    />
                    <Legend />
                    <Line 
                      yAxisId="left"
                      type="monotone" 
                      dataKey="revenue" 
                      stroke="#10b981" 
                      strokeWidth={2}
                      dot={{ fill: '#10b981', r: 4 }}
                      name="Revenue ($)"
                    />
                    <Bar 
                      yAxisId="right"
                      dataKey="orders" 
                      fill="#3b82f6" 
                      radius={[4, 4, 0, 0]}
                      name="Orders"
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Top Items Tab */}
        <TabsContent value="top-items">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Top Items by Quantity</CardTitle>
              </CardHeader>
              <CardContent>
                {topItems?.by_quantity && (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart 
                      data={formatTopItemsData(topItems.by_quantity)} 
                      layout="vertical"
                      margin={{ left: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis type="number" tick={{ fontSize: 12 }} />
                      <YAxis 
                        dataKey="name" 
                        type="category" 
                        width={100}
                        tick={{ fontSize: 11 }}
                      />
                      <Tooltip 
                        contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                        formatter={(value, name, props) => [value, props.payload.fullName]}
                      />
                      <Bar dataKey="value" fill="#10b981" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Top Items by Revenue</CardTitle>
              </CardHeader>
              <CardContent>
                {topItems?.by_revenue && (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={formatTopItemsData(topItems.by_revenue)}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        nameKey="name"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        labelLine={false}
                      >
                        {formatTopItemsData(topItems.by_revenue).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                        formatter={(value) => [`$${value}`, 'Revenue']}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Detailed Table */}
          {topItems?.detailed && topItems.detailed.length > 0 && (
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="text-lg">Item Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-4 font-medium text-gray-600">Item</th>
                        <th className="text-right py-2 px-4 font-medium text-gray-600">Quantity Sold</th>
                        <th className="text-right py-2 px-4 font-medium text-gray-600">Revenue</th>
                        <th className="text-right py-2 px-4 font-medium text-gray-600">Orders</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topItems.detailed.map((item, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="py-2 px-4">{item.name}</td>
                          <td className="text-right py-2 px-4">{item.quantity_sold}</td>
                          <td className="text-right py-2 px-4">${item.revenue.toFixed(2)}</td>
                          <td className="text-right py-2 px-4">{item.order_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Inventory Tab */}
        <TabsContent value="inventory">
          <div className="grid md:grid-cols-3 gap-4 mb-6">
            {inventory?.summary && (
              <>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-gray-500">Current Inventory</p>
                    <p className="text-2xl font-bold">{inventory.summary.current_inventory} items</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-gray-500">Sold (30 days)</p>
                    <p className="text-2xl font-bold text-green-600">{inventory.summary.items_sold_30d} items</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-gray-500">Turnover Rate</p>
                    <p className="text-2xl font-bold text-blue-600">{inventory.summary.turnover_rate}x</p>
                    {inventory.summary.days_of_inventory && (
                      <p className="text-sm text-gray-400 mt-1">
                        ~{Math.round(inventory.summary.days_of_inventory)} days of inventory
                      </p>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Turnover by Category</CardTitle>
            </CardHeader>
            <CardContent>
              {inventory?.by_category && inventory.by_category.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-4 font-medium text-gray-600">Category</th>
                        <th className="text-right py-2 px-4 font-medium text-gray-600">Inventory</th>
                        <th className="text-right py-2 px-4 font-medium text-gray-600">Sold (30d)</th>
                        <th className="text-right py-2 px-4 font-medium text-gray-600">Turnover</th>
                        <th className="text-left py-2 px-4 font-medium text-gray-600">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inventory.by_category.map((cat, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="py-2 px-4">{cat.category}</td>
                          <td className="text-right py-2 px-4">{cat.inventory}</td>
                          <td className="text-right py-2 px-4">{cat.sold_30d}</td>
                          <td className="text-right py-2 px-4">{cat.turnover}x</td>
                          <td className="py-2 px-4">
                            {cat.turnover > 1 ? (
                              <Badge className="bg-green-100 text-green-700">Fast moving</Badge>
                            ) : cat.turnover > 0.5 ? (
                              <Badge className="bg-yellow-100 text-yellow-700">Normal</Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-700">Slow moving</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No inventory data available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Scheduled Reports Tab */}
        <TabsContent value="scheduled">
          <ScheduledReportsManager userRole="DRLP" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
